import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ArrowRight, CreditCard } from "lucide-react";
import { API } from "../config";

const CheckoutResultPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async (sid, attempt) => {
    if (attempt >= 5) {
      setStatus("timeout");
      return;
    }
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`${API}/payments/checkout/status/${sid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPaymentData(response.data);
      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }
      setAttempts(attempt + 1);
    } catch (error) {
      /* error handled */;
      setAttempts(attempt + 1);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    pollStatus(sessionId, 0);
  }, [sessionId, pollStatus]);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6] flex items-center justify-center" data-testid="checkout-result-page">
      <div className="max-w-md w-full px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bento-card p-8 text-center">
          {status === "loading" && (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 size={32} className="text-blue-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-[#5D4037]">Verificando pago...</h2>
              <p className="text-stone-500 mt-2">Espera un momento mientras confirmamos tu pago</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-[#5D4037]">¡Pago exitoso!</h2>
              <p className="text-stone-500 mt-2">Tu compra ha sido procesada correctamente</p>
              {paymentData && (
                <div className="bg-[#2E7D32] text-white rounded-xl p-4 mt-6">
                  <p className="text-sm text-white/70">Total pagado</p>
                  <p className="text-3xl font-black">${(paymentData.amount_total / 100).toLocaleString("es-MX")} {paymentData.currency?.toUpperCase()}</p>
                </div>
              )}
              <div className="flex flex-col gap-3 mt-6">
                <Link to="/mis-guias" className="btn-primary flex items-center justify-center gap-2" data-testid="go-to-guides-btn">
                  <CreditCard size={18} /> Ir a Mis Guías
                </Link>
                <Link to="/cotizar" className="btn-secondary flex items-center justify-center gap-2">
                  Cotizar envío <ArrowRight size={18} />
                </Link>
              </div>
            </>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-[#5D4037]">
                {status === "timeout" ? "No pudimos confirmar el pago" : "Pago no completado"}
              </h2>
              <p className="text-stone-500 mt-2">
                {status === "timeout"
                  ? "Si realizaste el pago, tu saldo se actualizará en unos minutos."
                  : "El pago fue cancelado o expiró. Intenta de nuevo."}
              </p>
              <Link to="/guias-prepago" className="btn-primary mt-6 inline-flex items-center gap-2">
                Intentar de nuevo <ArrowRight size={18} />
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutResultPage;
